import { useState, useEffect, useCallback,useMemo } from "react";
import { useClientApi } from "../../../hooks/useClientApi";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { useClientDesignationApi } from "../../../hooks/useClientDesignationApi";
import { useResourceApi } from "../../../hooks/useResourceApi";
import { useRbac } from "../../../hooks/RbacContext";

export const useAllProjectsViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [clientPocs, setClientPocs] = useState([]);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [projectOwners, setProjectOwners] = useState([]);
  const { permissions } = useRbac();

  const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map(id => parseInt(id.trim()))
    : [];

  const isSuperAdmin = superAdminRoleIds.includes(parseInt(permissions?.globalRole?.role_id));

  console.log(
    "isSuperAdmin",
    isSuperAdmin,
    parseInt(permissions?.globalRole?.role_id),
    superAdminRoleIds
  );
  const {
    getResourcesByRoleIds,
    getResourcesByRoleCategoryId,
    cacheOperations: { readResources, writeResources, readResourcesByRoleIds },
  } = useResourceApi();

  const {
    getClients,
    getClientPocByClientId,
    cacheOperations: { readClients, writeClients },
  } = useClientApi();

  const {
    getProjects,
    createProject: apiCreateProject,
    updateProject: apiUpdateProject,
    deleteProject,
    cacheOperations: { readProjects, writeProjects },
  } = useProjectApi();
  const {
    getClientDesignations,
    cacheOperations: { readClientDesignations, writeClientDesignations },
  } = useClientDesignationApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [
        resourceResult,
        projectsResult,
        clientsResult,
        designationsResult,
        projectOwnersResult
      ] = await Promise.all([
        getResourcesByRoleIds(
          process.env.REACT_APP_PM_ROLE_IDS
            ? process.env.REACT_APP_PM_ROLE_IDS.split(",")
            : []
        ),
        getProjects(isSuperAdmin),
        getClients("active"),
        getClientDesignations(),
        getResourcesByRoleCategoryId(process.env.REACT_APP_LEADERSHIP_ROLE_ID)
      ]);

      if (
        !resourceResult.success ||
        !projectsResult.success ||
        !clientsResult.success ||
        !designationsResult.success ||
        !projectOwnersResult.success
      ) {
        throw new Error("Failed to fetch required data");
      }
      writeResources(resourceResult.data);
      setProjectOwners(projectOwnersResult.data);
      writeProjects(projectsResult.data, isSuperAdmin);
      writeClients(clientsResult.data);
      writeClientDesignations(designationsResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [
    getProjects,
    getClients,
    getClientDesignations,
    getResourcesByRoleIds,
    getResourcesByRoleCategoryId,
    writeProjects,
    writeClients,
    writeClientDesignations,
    loading,
  ]);

  const fetchClientPocs = useCallback(
    async (clientId) => {
      if (currentClientId === clientId) {
        return clientPocs;
      }

      try {
        setCurrentClientId(clientId);
        const result = await getClientPocByClientId(clientId);
        if (result.success) {
          setClientPocs(result.data || []);
          return result.data;
        }
        setClientPocs([]);
        return [];
      } catch (error) {
        console.error("Error fetching client POCs:", error);
        setClientPocs([]);
        return [];
      }
    },
    [currentClientId, clientPocs]
  );
  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateProject = useCallback(
    async (formData) => {
      try {
        const result = await apiCreateProject(formData, isSuperAdmin);
        if (result.success) {
          setSuccessModal({
            type: "success",
            title: "Success",
            message: "Project created successfully",
          });
          return { success: true };
        }
        throw new Error(result.error || "Failed to create project");
      } catch (error) {
        console.error("Error creating project:", error);
        // setSuccessModal({
        //   type: "error",
        //   title: "Action Failed",
        //   message: error.message || "An unexpected error occurred",
        // });
        throw error;
      }
    },
    [apiCreateProject]
  );

  const handleUpdateProject = useCallback(
    async (projectId, formData) => {
      try {
        const result = await apiUpdateProject(
          projectId,
          formData,
          isSuperAdmin
        );
        if (result.success) {
          setSuccessModal({
            type: "success",
            title: "Success",
            message: "Project updated successfully",
          });
          return { success: true };
        }
        throw new Error(result.error || "Failed to update project");
      } catch (error) {
        console.error("Error updating project:", error);
        // setSuccessModal({
        //   type: "error",
        //   title: "Action Failed",
        //   message: error.message || "An unexpected error occurred",
        // });
        throw error;
      }
    },
    [apiUpdateProject]
  );

  const handleDelete = useCallback(
    async (project) => {
      try {
        const result = await deleteProject(project.project_id, isSuperAdmin);
        if (result.success) {
          setSuccessModal({
            type: "success",
            title: "Success",
            message: "Project deleted successfully",
          });
        } else {
          setSuccessModal({
            type: "error",
            title: "Action Failed",
            message: result.error || "Failed to delete client",
          });
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting project:", err);
      }
    },
    [deleteProject]
  );

  const getClientName = useCallback(
    (clientId) => {
      const clients = readClients();
      const client = clients.find(
        (c) => c.client_id.toString() === clientId?.toString()
      );
      return client?.client_name || "N/A";
    },
    [readClients]
  );
    const getProjectName = useMemo(
      () => (projectId) => {
        const projects = readProjects(isSuperAdmin);
  
        const project = projects.find(
          (project) => project.project_id.toString() === projectId?.toString()
        );
  
        return project?.project_name || "Unknown";
      },
      [readProjects]
    );

  return {
    projects: readProjects(isSuperAdmin),
    clients: readClients(),
    resources: readResourcesByRoleIds(
      process.env.REACT_APP_PM_ROLE_IDS
        ? process.env.REACT_APP_PM_ROLE_IDS.split(",")
        : []
    ),
    projectOwners,
    clientDesignations: readClientDesignations(),
    loading,
    error,
    getProjectName,
    getClientName,
    createProject: handleCreateProject,
    updateProject: handleUpdateProject,
    handleDelete,
    successModal,
    setSuccessModal,
    refetch: fetchData,
    fetchClientPocs,
    clientPocs,
  };
};
